import { useGetDownloadSignedUrlForQuestionsQuery } from '@exam-room/graphql';
import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useState } from 'react';

import {
  precacheSignedUrls,
  isServiceWorkerActive,
  type SignedImagePayload,
  type PrecacheProgressPayload,
} from '../../../services/serviceWorker';
import { type Choice, type QuestionWithOrder, type CacheStatus } from '../types';
import { mapSignedUrlToImages, mapSignedUrlToSyntheticUrls } from '../utils';

export function useGetQuestionsWithImage(
  questions: QuestionWithOrder[],
  examId?: string,
  refetchKey?: number,
) {
  const [parsedQuestions, setParsedQuestions] = useState<QuestionWithOrder[]>(questions);
  const [isMappingQuestionWithImages, setIsMappingQuestionWithImages] = useState(false);
  const [cacheStatus, setCacheStatus] = useState<CacheStatus>({
    isCaching: false,
    cachedCount: 0,
    totalCount: 0,
    isComplete: false,
  });

  const {
    data: getSignedUrlsData,
    loading: isSignedUrlsLoading,
    error: getSignedUrlsError,
    refetch: refetchSignedUrls,
  } = useGetDownloadSignedUrlForQuestionsQuery({
    variables: {
      input: {
        examId: examId as string,
      },
    },
    skip: !examId,
  });

  // Refetch when refetchKey changes
  useEffect(() => {
    if (refetchKey && refetchKey > 0 && examId) {
      refetchSignedUrls();
    }
  }, [refetchKey, examId, refetchSignedUrls]);

  // cspell:disable-next-line
  const handleProgress = useCallback((payload: PrecacheProgressPayload) => {
    setCacheStatus((prev) => ({
      ...prev,
      cachedCount: payload.count,
    }));
  }, []);

  useEffect(() => {
    if (getSignedUrlsData?.erGetDownloadSignedUrlForQuestions?.signedUrls) {
      setIsMappingQuestionWithImages(true);
      const imgUrls: Record<string, Record<string, string>> = getSignedUrlsData
        .erGetDownloadSignedUrlForQuestions.signedUrls;

      // Step 1: Map signed URLs to images (for online viewing)
      const parsedWithSignedUrls = questions.map((question) => ({
        ...question,
        question: mapSignedUrlToImages(question.question, imgUrls[question.id]),
        metadata: {
          ...question.metadata,
          paragraphs: mapSignedUrlToImages(question.metadata?.paragraphs, imgUrls[question.id]),
          choices: question.metadata?.choices?.map((choice: Choice) => ({
            ...choice,
            label: mapSignedUrlToImages(choice.label, imgUrls[question.id]),
          })),
        },
      }));

      // Step 2: Prepare images for caching
      const imagesToCache: SignedImagePayload[] = [];
      Object.entries(imgUrls).forEach(([questionId, urlMap]) => {
        Object.entries(urlMap).forEach(([filename, signedUrl]) => {
          // Create unique ID for each image: questionId/filename
          const imageId = `${questionId}/${filename}`;
          imagesToCache.push({ id: imageId, signedUrl });
        });
      });

      // Step 3: Cache images in service worker if available
      if (isServiceWorkerActive() && imagesToCache.length > 0) {
        // console.log('[useGetQuestionsWithImage] Starting to cache', imagesToCache.length, 'images');
        setCacheStatus({
          isCaching: true,
          cachedCount: 0,
          totalCount: imagesToCache.length,
          isComplete: false,
        });

        precacheSignedUrls(imagesToCache, handleProgress)
          .then((result) => {
            // console.log('[useGetQuestionsWithImage] Caching complete:', result);
            const cacheErrorMessage =
              result.failed > 0 ? `${result.failed} images failed to cache` : undefined;
            setCacheStatus({
              isCaching: false,
              cachedCount: result.count,
              totalCount: result.total,
              isComplete: result.ok && result.failed === 0,
              error: cacheErrorMessage,
            });

            // Step 4: Map to synthetic URLs for offline access
            const parsedWithSyntheticUrls = questions.map((question) => ({
              ...question,
              question: mapSignedUrlToSyntheticUrls(
                question.question,
                question.id,
                imgUrls[question.id],
              ),
              metadata: {
                ...question.metadata,
                paragraphs: mapSignedUrlToSyntheticUrls(
                  question.metadata?.paragraphs,
                  question.id,
                  imgUrls[question.id],
                ),
                choices: question.metadata?.choices?.map((choice: Choice) => ({
                  ...choice,
                  label: mapSignedUrlToSyntheticUrls(
                    choice.label,
                    question.id,
                    imgUrls[question.id],
                  ),
                })),
              },
            }));

            setParsedQuestions(parsedWithSyntheticUrls);
          })
          .catch((err) => {
            console.error('[useGetQuestionsWithImage] Failed to cache images:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to cache images';
            setCacheStatus({
              isCaching: false,
              cachedCount: 0,
              totalCount: imagesToCache.length,
              isComplete: false,
              error: errorMessage,
            });
            // Fall back to signed URLs if caching fails
            setParsedQuestions(parsedWithSignedUrls);
            Sentry.captureException(err, {
              tags: {
                feature: 'question-image-caching',
              },
              extra: {
                examId,
                imagesToCache,
              },
            });
          })
          .finally(() => {
            setIsMappingQuestionWithImages(false);
          });
      } else {
        // No service worker or no images - just use signed URLs
        // console.warn('[useGetQuestionsWithImage] Service worker not active or no images to cache');
        setParsedQuestions(parsedWithSignedUrls);
        setIsMappingQuestionWithImages(false);
      }
    }
  }, [
    questions,
    getSignedUrlsData?.erGetDownloadSignedUrlForQuestions?.signedUrls,
    handleProgress,
  ]);

  return {
    data: parsedQuestions,
    loading: isSignedUrlsLoading || isMappingQuestionWithImages,
    cacheStatus,
    error: getSignedUrlsError,
  };
}
